import { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Button, Col, Divider, Flex, Form, Input, InputNumber, Modal, Row, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { ExperienceDuration } from "pagesV2/pro/Experiences/ListingScheduling/LengthAndRates/ExperienceDuration"
import { BufferTime } from "pagesV2/pro/Experiences/ListingScheduling/LengthAndRates/BufferTime"
import { PricesAndTypes } from "pagesV2/pro/Experiences/ListingScheduling/LengthAndRates/PricesAndTypes"
import { AllowedTypesFormItem } from "pagesV2/pro/Experiences/ListingScheduling/LengthAndRates/FlatRateFormItems/AllowedTypesFormItem"
import { durationRule, PRICING_TYPES, TRIP_PRICE } from "pagesV2/pro/Experiences/ListingScheduling/utils/constants"
import { InfoBox } from "ui/InfoBox"
import { InfoTooltip } from "ui/Tooltip/InfoTooltip"
import { Text } from "ui/Typography"
import { spacings } from "themes/variables"
import CustomDrawer from "ui/Drawer"
import { useDevice } from "hooks/useDevice"
import { numberToCurrency } from "helpers/string"
import styles from "./AddBookingOptionModal.module.css"

const PRICE_FIELDS = [
  TRIP_PRICE.DURATION,
  TRIP_PRICE.BUFFER_TIME,
  TRIP_PRICE.PRICE_ADULT,
  TRIP_PRICE.PRICE_CHILD,
  TRIP_PRICE.PRICE_INFANT,
  TRIP_PRICE.PRICE_PASSENGER,
  TRIP_PRICE.FLAT_RATE,
  TRIP_PRICE.GUESTS_INCLUDED,
  "adults_allowed",
  "children_allowed",
  "infants_allowed",
  "passengers_allowed",
  "extra_guests_allowed"
]

export const AddBookingOptionModal = ({ open, onClose, record, wizardForm, isFlatRate, onPriceSave, isDayBased }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const [modalForm] = Form.useForm()
  const isEditing = !!(record?.id && typeof record.id === "number")
  const [priceError, setPriceError] = useState("")

  const maxGuests = Form.useWatch("max_guests", wizardForm)

  const guestsIncluded = Form.useWatch(TRIP_PRICE.GUESTS_INCLUDED, modalForm)
  const flatRateValue = Form.useWatch(TRIP_PRICE.FLAT_RATE, modalForm)
  const extraGuestPrice = Form.useWatch(TRIP_PRICE.PRICE_ADULT, modalForm)

  const notIncluded = typeof maxGuests === "number" && typeof guestsIncluded === "number" ? Math.max(0, maxGuests - guestsIncluded) : null

  useEffect(() => {
    if (!open) return
    if (record) {
      const initial = {}
      PRICE_FIELDS.forEach((key) => {
        initial[key] = record[key] ?? undefined
      })
      // Adults are available by default — keep them on unless explicitly disabled.
      initial[TRIP_PRICE.ADULTS_ALLOWED] = record[TRIP_PRICE.ADULTS_ALLOWED] ?? true
      modalForm.setFieldsValue(initial)
    } else {
      modalForm.resetFields()
    }
  }, [open, record, modalForm])

  const handleClose = () => {
    modalForm.resetFields()
    setPriceError("")
    onClose()
  }

  const handleSave = async () => {
    try {
      // Validate every field so each unfilled required field shows its inline
      // error (Duration, Flat Rate, Guests Included, Charges per Additional Guest…).
      await modalForm.validateFields()
    } catch {
      // Invalid — AntD has rendered the inline errors; keep the modal open.
      return
    }

    if (!isFlatRate) {
      const values = modalForm.getFieldsValue(true)
      const enabledPricePairs = [
        { allowed: TRIP_PRICE.ADULTS_ALLOWED, price: TRIP_PRICE.PRICE_ADULT },
        { allowed: TRIP_PRICE.CHILDREN_ALLOWED, price: TRIP_PRICE.PRICE_CHILD },
        { allowed: TRIP_PRICE.INFANTS_ALLOWED, price: TRIP_PRICE.PRICE_INFANT },
        { allowed: TRIP_PRICE.PASSENGERS_ALLOWED, price: TRIP_PRICE.PRICE_PASSENGER }
      ]
      const hasAtLeastOnePrice = enabledPricePairs.some(({ allowed, price }) => values[allowed] && Number(values[price]) > 0)
      if (!hasAtLeastOnePrice) {
        setPriceError(t("experiences.pricing.private.at_least_one_price_required"))
        return
      }
    }
    setPriceError("")
    const values = modalForm.getFieldsValue(true)
    const priceData = {}
    PRICE_FIELDS.forEach((key) => {
      if (values[key] !== undefined) priceData[key] = values[key]
    })
    // Null out prices for disabled guest types so stale values don't appear in the summary
    if (!values[TRIP_PRICE.CHILDREN_ALLOWED]) priceData[TRIP_PRICE.PRICE_CHILD] = null
    if (!values[TRIP_PRICE.INFANTS_ALLOWED]) priceData[TRIP_PRICE.PRICE_INFANT] = null
    if (!values[TRIP_PRICE.PASSENGERS_ALLOWED]) priceData[TRIP_PRICE.PRICE_PASSENGER] = null
    priceData.pricing_type = isFlatRate ? PRICING_TYPES.FLAT_RATE : PRICING_TYPES.PER_PERSON

    if (isEditing) {
      onPriceSave({ ...priceData, id: record.id })
    } else {
      const tempId = record?.tempId
      onPriceSave(tempId == null ? priceData : { ...priceData, tempId })
    }
    handleClose()
  }

  const title = isEditing ? t("experiences.pricing.private.edit_booking_option") : t("experiences.pricing.private.add_booking_option")

  const showSummary = notIncluded !== null && typeof flatRateValue === "number" && typeof guestsIncluded === "number"

  const content = (
    <Form form={modalForm} layout="vertical" initialValues={{ [TRIP_PRICE.ADULTS_ALLOWED]: true }}>
      <div className={styles.formCard}>
        <Row gutter={[16, 16]}>
          <Col span={24} md={12}>
            <Form.Item
              name={TRIP_PRICE.DURATION}
              label={t("experiences.pricing.private.duration_label")}
              required
              rules={[durationRule(t)]}
              className="mb-0"
            >
              <ExperienceDuration form={wizardForm} expandedRowData={record} isDayBased={isDayBased} />
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              name={TRIP_PRICE.BUFFER_TIME}
              label={
                <Flex align="center" gap="small">
                  {t("experiences.scheduling.sections.length_and_rates.buffer_time")}
                  <InfoTooltip title={t("experiences.scheduling.sections.length_and_rates.buffer_time_info")} />
                </Flex>
              }
              className="mb-0"
            >
              <BufferTime form={modalForm} expandedRowData={record} />
            </Form.Item>
          </Col>
        </Row>

        <Divider className="my-l" />

        {isFlatRate ? (
          <Flex vertical gap={spacings.XL}>
            <Row gutter={16}>
              <Col span={24} md={12}>
                <Form.Item
                  name={TRIP_PRICE.GUESTS_INCLUDED}
                  label={
                    <Flex align="center" gap="small">
                      {t("experiences.pricing.private.guests_included_flat_rate")}
                      <InfoTooltip title={t("experiences.pricing.private.guests_included_flat_rate_tooltip")} />
                    </Flex>
                  }
                  rules={[{ required: true, message: t("form_validation.cannot_be_blank") }]}
                  className="mb-0"
                >
                  <InputNumber
                    size="large"
                    min={1}
                    max={maxGuests}
                    suffix={maxGuests ? <span className={styles.capacitySuffix}>{`/${maxGuests}`}</span> : undefined}
                    className="w-100"
                  />
                </Form.Item>
              </Col>
              <Col span={24} md={12}>
                <Form.Item
                  name={TRIP_PRICE.FLAT_RATE}
                  label={t("experiences.pricing.private.set_flat_base_rate")}
                  rules={[{ required: true, message: t("form_validation.cannot_be_blank") }]}
                  className="mb-0"
                >
                  <InputNumber prefix="$" size="large" min={0} className="w-100" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24} md={12}>
                <Flex vertical gap={spacings.XS}>
                  <Flex align="center" gap="small">
                    <Text fw={500} size="m">
                      {t("experiences.pricing.private.not_included_flat_rate")}
                    </Text>
                    <InfoTooltip title={t("experiences.pricing.private.not_included_flat_rate_tooltip")} />
                  </Flex>
                  <Input
                    size="large"
                    readOnly
                    value={notIncluded === null ? "" : `${notIncluded}`}
                    suffix={notIncluded === null ? undefined : <span className={styles.capacitySuffix}>{`/${maxGuests}`}</span>}
                    className={styles.readonlyInput}
                  />
                </Flex>
              </Col>
              <Col span={24} md={12}>
                <Form.Item
                  name={TRIP_PRICE.PRICE_ADULT}
                  label={t("experiences.pricing.private.charges_per_additional_guest")}
                  required={notIncluded > 0}
                  // Always-registered validator (not a conditional rules array) so
                  // validateFields() reliably runs it; required only when there are
                  // guests beyond the flat-rate allowance (notIncluded > 0).
                  rules={[
                    {
                      validator: (_rule, value) =>
                        notIncluded > 0 && (value === undefined || value === null || value === "")
                          ? Promise.reject(new Error(t("form_validation.cannot_be_blank")))
                          : Promise.resolve()
                    }
                  ]}
                  className="mb-0"
                >
                  <InputNumber prefix="$" size="large" min={0} disabled={notIncluded === 0 || notIncluded === null} className="w-100" />
                </Form.Item>
              </Col>
            </Row>

            {showSummary && (
              <InfoBox>
                {t("experiences.pricing.private.flat_rate_summary", {
                  max: maxGuests,
                  included: guestsIncluded,
                  flatRate: numberToCurrency(flatRateValue ?? 0),
                  notIncluded,
                  extraPrice: numberToCurrency(extraGuestPrice ?? 0)
                })}
              </InfoBox>
            )}

            <Divider className="my-0" />

            <Flex vertical gap={spacings.S}>
              <Text fw={500} size="l">
                {t("experiences.pricing.private.guests_types_allowed_title")}
              </Text>
              <AllowedTypesFormItem
                name={TRIP_PRICE.ADULTS_ALLOWED}
                text={t("experiences.scheduling.sections.length_and_rates.adults")}
                subText={t("experiences.scheduling.sections.length_and_rates.available_by_default")}
              />
              <AllowedTypesFormItem
                name={TRIP_PRICE.CHILDREN_ALLOWED}
                text={t("experiences.scheduling.sections.length_and_rates.children")}
                subText={t("experiences.scheduling.sections.length_and_rates.under_12_years")}
              />
              <AllowedTypesFormItem
                name={TRIP_PRICE.INFANTS_ALLOWED}
                text={t("experiences.scheduling.sections.length_and_rates.infants")}
                subText={t("experiences.scheduling.sections.length_and_rates.under_2_years")}
              />
            </Flex>
          </Flex>
        ) : (
          <PricesAndTypes form={modalForm} title={t("experiences.pricing.private.guests_types_title")} record={record} />
        )}
      </div>
      <Flex vertical gap={spacings.XS} className="mt-l">
        {priceError && <Typography.Text type="danger">{priceError}</Typography.Text>}
        <Flex gap={spacings.S}>
          <Button size="large" onClick={handleClose}>
            {t("global.cancel")}
          </Button>
          <Button size="large" color="primary" variant="solid" onClick={handleSave}>
            {t("global.save")}
          </Button>
        </Flex>
      </Flex>
    </Form>
  )

  if (isMobile) {
    return (
      <CustomDrawer open={open} setOpen={() => handleClose()} title={title}>
        {content}
      </CustomDrawer>
    )
  }

  return (
    <Modal open={open} onCancel={handleClose} title={title} footer={null} width={700} centered destroyOnClose>
      {content}
    </Modal>
  )
}

AddBookingOptionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  record: PropTypes.object,
  wizardForm: PropTypes.object.isRequired,
  isFlatRate: PropTypes.bool,
  onPriceSave: PropTypes.func.isRequired,
  isDayBased: PropTypes.bool,
}
